import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type MouseEvent,
} from 'react';
import { TransportBar } from '../components/TransportBar';
import { RhythmPatternGrid } from '../components/RhythmPatternGrid';
import { RhythmPulseGrid } from '../components/RhythmPulseGrid';
import { RhythmCreatorView } from '../components/RhythmCreatorView';
import { useRhythmPlayback } from '../hooks/useRhythmPlayback';
import { RHYTHM_CHALLENGES, type RhythmChallenge } from '../data/rhythmChallenges';
import { applyRhythmFigureToBeat } from '../utils/rhythmPattern';
import {
  beatFeedbackMap,
  cloneRhythmPattern,
  compareRhythmPatterns,
  emptyGuessFromAnswer,
} from '../utils/rhythmCompare';
import {
  deleteCustomRhythm,
  loadCustomRhythms,
  type CustomRhythmChallenge,
} from '../utils/customRhythms';
import type { NoteFigure } from '../types';
import type { RhythmPattern } from '../utils/rhythmPattern';
import type { RhythmCompareResult } from '../utils/rhythmCompare';

type Phase = 'pick' | 'create' | 'listen' | 'discover' | 'result';

interface Props {
  onBack: () => void;
}

const STORAGE_SOUND = 'boom-sound-enabled';

export function DiscoverRhythmApp({ onBack }: Props) {
  const [phase, setPhase] = useState<Phase>('pick');
  const [customRhythms, setCustomRhythms] = useState<CustomRhythmChallenge[]>([]);
  const [editingCustom, setEditingCustom] = useState<CustomRhythmChallenge | null>(null);
  const [challenge, setChallenge] = useState<RhythmChallenge | null>(null);
  const [answerKey, setAnswerKey] = useState<RhythmPattern | null>(null);
  const [guess, setGuess] = useState<RhythmPattern | null>(null);
  const [selectedFigure, setSelectedFigure] = useState<NoteFigure>('negra');
  const [bpm, setBpm] = useState(72);
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem(STORAGE_SOUND) !== 'false',
  );
  const [compareResult, setCompareResult] = useState<RhythmCompareResult | null>(null);
  const [listenCount, setListenCount] = useState(0);

  const accent = '#7b1fa2';
  const playbackPattern =
    (phase === 'listen' ? answerKey : guess) ?? RHYTHM_CHALLENGES[0].pattern;

  const { playing, activeIndex, play, stop } = useRhythmPlayback(
    playbackPattern,
    bpm,
    soundEnabled,
  );

  useEffect(() => {
    setCustomRhythms(loadCustomRhythms());
  }, [phase]);

  const refreshCustom = () => setCustomRhythms(loadCustomRhythms());

  const startChallenge = (c: RhythmChallenge) => {
    setChallenge(c);
    setAnswerKey(cloneRhythmPattern(c.pattern));
    setGuess(emptyGuessFromAnswer(c.pattern));
    setBpm(c.bpm);
    setCompareResult(null);
    setListenCount(0);
    setPhase('listen');
  };

  const handleListen = () => {
    if (!answerKey) return;
    setListenCount((n) => n + 1);
    void play();
  };

  const goDiscover = () => {
    stop();
    setPhase('discover');
  };

  const handleApplyFigure = (measureIndex: number, beatIndex: number) => {
    if (!guess) return;
    setGuess((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        measures: prev.measures.map((m, mi) =>
          mi !== measureIndex
            ? m
            : applyRhythmFigureToBeat(m, beatIndex, selectedFigure, prev.beatsPerMeasure),
        ),
      };
    });
  };

  const handleCellToggle = (
    measureIndex: number,
    beatIndex: number,
    cellIndex: number,
  ) => {
    if (!guess) return;
    setGuess((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        measures: prev.measures.map((m, mi) => {
          if (mi !== measureIndex) return m;
          return {
            slots: m.slots.map((slot, bi) => {
              if (bi !== beatIndex || !slot.active) return slot;
              return {
                ...slot,
                cells: slot.cells.map((c, ci) =>
                  ci === cellIndex ? { ...c, hit: !c.hit } : c,
                ),
              };
            }),
          };
        }),
      };
    });
  };

  const handleCheck = () => {
    if (!answerKey || !guess) return;
    stop();
    const result = compareRhythmPatterns(answerKey, guess);
    setCompareResult(result);
    setPhase('result');
  };

  const handleRetry = () => {
    if (!challenge) return;
    setGuess(emptyGuessFromAnswer(challenge.pattern));
    setCompareResult(null);
    setPhase('discover');
  };

  const handleListenAgain = () => {
    setCompareResult(null);
    setPhase('listen');
  };

  const handleDeleteCustom = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    if (!confirm('¿Borrar este ritmo?')) return;
    deleteCustomRhythm(id);
    refreshCustom();
  };

  const feedback = useMemo(
    () => (compareResult ? beatFeedbackMap(compareResult) : undefined),
    [compareResult],
  );

  const cssVars = useMemo(
    () =>
      ({
        '--accent': accent,
        '--rhythm-accent': accent,
      }) as CSSProperties,
    [],
  );

  if (phase === 'create') {
    return (
      <RhythmCreatorView
        editChallenge={editingCustom}
        onBack={() => {
          setEditingCustom(null);
          setPhase('pick');
          refreshCustom();
        }}
        onUseWithClass={(c) => {
          setEditingCustom(null);
          refreshCustom();
          startChallenge(c);
        }}
      />
    );
  }

  if (phase === 'pick') {
    return (
      <div className="app app--discover" style={cssVars}>
        <header className="app-header">
          <div>
            <button type="button" className="btn-back" onClick={onBack}>
              ← Inicio
            </button>
            <h1 className="app-title">Descubre el ritmo</h1>
            <p className="app-subtitle">Escucha y adivina las figuras de cada tiempo</p>
          </div>
        </header>

        <button
          type="button"
          className="challenge-card challenge-card--create"
          onClick={() => {
            setEditingCustom(null);
            setPhase('create');
          }}
        >
          <span className="challenge-card__create-icon" aria-hidden="true">
            +
          </span>
          <h3 className="challenge-card__title">Crear ritmo</h3>
          <p className="challenge-card__desc">
            Arma tu propio patrón y úsalo como reto con la clase
          </p>
        </button>

        {customRhythms.length > 0 && (
          <>
            <h2 className="challenge-section-title">Mis ritmos</h2>
            <ul className="challenge-list">
              {customRhythms.map((c) => (
                <li key={c.id} className="challenge-list__item">
                  <button
                    type="button"
                    className="challenge-card challenge-card--custom"
                    onClick={() => startChallenge(c)}
                  >
                    <h3 className="challenge-card__title">{c.title}</h3>
                    <p className="challenge-card__desc">{c.description}</p>
                    <span className="challenge-card__meta">
                      {c.pattern.measures.length} compás(es) · {c.bpm} BPM
                    </span>
                  </button>
                  <div className="challenge-card__actions">
                    <button
                      type="button"
                      className="btn-icon challenge-card__edit"
                      onClick={() => {
                        setEditingCustom(c);
                        setPhase('create');
                      }}
                      aria-label="Editar"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="btn-icon challenge-card__delete"
                      onClick={(e) => handleDeleteCustom(c.id, e)}
                      aria-label="Borrar"
                    >
                      🗑
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        <h2 className="challenge-section-title">Retos incluidos</h2>
        <ul className="challenge-list">
          {RHYTHM_CHALLENGES.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="challenge-card"
                onClick={() => startChallenge(c)}
              >
                <h3 className="challenge-card__title">{c.title}</h3>
                <p className="challenge-card__desc">{c.description}</p>
                <span className="challenge-card__meta">
                  {c.pattern.measures.length} compás(es) · {c.bpm} BPM
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (!challenge || !answerKey || !guess) return null;

  return (
    <div
      className="app app--discover app--discover-session"
      style={cssVars}
    >
      <header className="discover-topbar">
        <button
          type="button"
          className="btn-back"
          onClick={() => {
            stop();
            setPhase('pick');
          }}
        >
          ←
        </button>
        <div className="discover-topbar__titles">
          <h1 className="discover-topbar__title">{challenge.title}</h1>
          <p className="discover-topbar__phase">
            {phase === 'listen' && 'Escucha el latido'}
            {phase === 'discover' && 'Descubre las figuras'}
            {phase === 'result' && 'Resultado'}
          </p>
        </div>
        {listenCount > 0 && phase === 'listen' && (
          <span className="discover-topbar__badge">{listenCount}×</span>
        )}
      </header>

      <main
        className={`discover-stage ${phase === 'listen' ? 'discover-stage--listen' : 'discover-stage--play'}`}
      >
        {phase === 'listen' && (
          <RhythmPulseGrid
            pattern={answerKey}
            activeIndex={activeIndex}
            playing={playing}
            accentColor={accent}
            discoverLayout
          />
        )}

        {(phase === 'discover' || phase === 'result') && (
          <>
            <div className="discover-toolbar">
              <TransportBar
                bpm={bpm}
                onBpmChange={setBpm}
                figure={selectedFigure}
                onFigureChange={setSelectedFigure}
                playing={playing}
                onPlay={play}
                onStop={stop}
                soundEnabled={soundEnabled}
                onSoundToggle={() => setSoundEnabled((s) => !s)}
                isFullscreen={false}
                onFullscreenToggle={() => {}}
                hideFullscreen
              />
            </div>

            {phase === 'result' && compareResult && (
              <div
                className={`discover-score discover-score--compact ${compareResult.allCorrect ? 'discover-score--win' : ''}`}
                role="status"
              >
                {compareResult.allCorrect ? (
                  <strong>🎉 ¡Perfecto!</strong>
                ) : (
                  <strong>
                    {compareResult.score}/{compareResult.total} tiempos
                  </strong>
                )}
              </div>
            )}

            <RhythmPatternGrid
              pattern={guess}
              accentColor={accent}
              activeIndex={activeIndex}
              selectedFigure={selectedFigure}
              beatFeedback={feedback}
              revealAnswer={phase === 'result' ? answerKey : undefined}
              readOnly={phase === 'result'}
              discoverLayout
              onCellToggle={handleCellToggle}
              onApplyFigure={handleApplyFigure}
            />
          </>
        )}
      </main>

      <footer className="discover-footer">
        {phase === 'listen' && (
          <>
            <label className="discover-bpm discover-bpm--inline">
              BPM
              <input
                type="range"
                min={50}
                max={100}
                step={2}
                value={bpm}
                onChange={(e) => setBpm(Number(e.target.value))}
              />
              <strong>{bpm}</strong>
            </label>
            <div className="discover-actions">
              <button
                type="button"
                className="btn btn-play"
                onClick={handleListen}
                disabled={playing}
              >
                {playing ? '♪…' : '▶ Escuchar'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={goDiscover}
                disabled={listenCount === 0}
              >
                Descubrir →
              </button>
            </div>
          </>
        )}

        {phase === 'discover' && (
          <div className="discover-actions">
            <button type="button" className="btn btn-secondary" onClick={handleListenAgain}>
              👂 Latido
            </button>
            <button type="button" className="btn btn-primary" onClick={handleCheck}>
              ✓ Comprobar
            </button>
          </div>
        )}

        {phase === 'result' && (
          <div className="discover-actions">
            <button type="button" className="btn btn-secondary" onClick={handleListenAgain}>
              👂 Latido
            </button>
            <button type="button" className="btn btn-primary" onClick={handleRetry}>
              Reintentar
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setPhase('pick')}>
              Retos
            </button>
          </div>
        )}
      </footer>
    </div>
  );
}
